import { useState, useCallback, useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Loader2,
  Check,
  ArrowRight,
  FileText,
  Sparkles,
  Link2,
  Users,
  ChevronLeft,
  Download,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInterviewerStore, type InterviewWorkflowStep } from '@/stores/interviewerStore'
import { InterviewSetup } from './InterviewSetup'
import { FacetDetectionPanel } from './FacetDetectionPanel'
import { OntologyMappingPanel } from './OntologyMappingPanel'
import type {
  InterviewTypeId,
  FacetMappingDecision,
  MappingAction,
  InputSource,
} from '@/types/interviewer'
import { getInterviewTypeConfig, getMappingSummary } from '@/types/interviewer'
import { exportInterviewToPdf } from '@/lib/interviewer/exportPdf'
import { debugLog } from '@/lib/debugLogger'
import { resolveEntityType } from '@/lib/interviewer/entityCreation'

interface GenericInterviewerProps {
  onCancel: () => void
  onComplete: () => void
}

const WORKFLOW_STEPS: { step: InterviewWorkflowStep; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { step: 'setup', label: 'Setup', icon: FileText },
  { step: 'analyzing', label: 'Analyzing', icon: Sparkles },
  { step: 'detection', label: 'Review', icon: Check },
  { step: 'mapping', label: 'Map', icon: Link2 },
  { step: 'complete', label: 'Done', icon: Check },
]

export function GenericInterviewer({ onCancel, onComplete }: GenericInterviewerProps) {
  const {
    currentSource,
    workflowStep,
    pendingMappingDecisions,
    createSource,
    analyzeSource,
    setWorkflowStep,
    addMappingDecision,
    applyMappingDecisions,
    resetWorkflow,
    clearCurrentSource,
  } = useInterviewerStore()

  // Selected facets for mapping (initially all)
  const [selectedFacetIds, setSelectedFacetIds] = useState<Set<string>>(new Set())

  // Initialize selected facets when analysis completes
  // NOTE: We read directly from the store to avoid stale closure issues
  const initializeSelectedFacets = useCallback(() => {
    // Get the latest source directly from the store (not from closure)
    const latestSource = useInterviewerStore.getState().currentSource
    console.log(`[GenericInterviewer] initializeSelectedFacets called`)
    console.log(`[GenericInterviewer] - closure currentSource.facets:`, currentSource?.facets?.length || 0)
    console.log(`[GenericInterviewer] - store currentSource.facets:`, latestSource?.facets?.length || 0)

    if (latestSource?.facets) {
      const activeFacets = latestSource.facets.filter(f => !f.dismissed)
      console.log(`[GenericInterviewer] Setting ${activeFacets.length} active facets as selected (${latestSource.facets.length} total, ${latestSource.facets.length - activeFacets.length} dismissed)`)
      setSelectedFacetIds(new Set(activeFacets.map(f => f.id)))
    } else {
      console.warn('[GenericInterviewer] initializeSelectedFacets: No facets available in store!')
    }
  }, [currentSource?.facets])

  // Handle setup submission
  const handleSetupSubmit = useCallback(async (data: {
    interviewTypeId: InterviewTypeId
    title: string
    content: string
    context: string
    linkedPersonId?: string
    linkedPersonName?: string
  }) => {
    // Get the source type from interview type
    const sourceType = data.interviewTypeId as string

    // Create the source
    const source = createSource(
      sourceType as InputSource['sourceType'],
      data.title,
      data.content,
      {
        context: data.context,
        interviewTypeId: data.interviewTypeId,
        linkedPersonId: data.linkedPersonId,
        linkedPersonName: data.linkedPersonName,
      }
    )

    // Move to analyzing step
    setWorkflowStep('analyzing')

    // Start analysis
    await analyzeSource(source.id)

    // Initialize selected facets and move to detection
    initializeSelectedFacets()
    setWorkflowStep('detection')
  }, [createSource, analyzeSource, setWorkflowStep, initializeSelectedFacets])

  // Handle facet selection toggle
  const handleToggleFacet = useCallback((facetId: string) => {
    setSelectedFacetIds(prev => {
      const next = new Set(prev)
      if (next.has(facetId)) {
        next.delete(facetId)
      } else {
        next.add(facetId)
      }
      return next
    })
  }, [])

  // Handle select/deselect all
  const handleSelectAll = useCallback(() => {
    if (currentSource?.facets) {
      const activeFacets = currentSource.facets.filter(f => !f.dismissed)
      setSelectedFacetIds(new Set(activeFacets.map(f => f.id)))
    }
  }, [currentSource?.facets])

  const handleDeselectAll = useCallback(() => {
    setSelectedFacetIds(new Set())
  }, [])

  // Proceed to mapping
  const handleProceedToMapping = useCallback(() => {
    setWorkflowStep('mapping')
  }, [setWorkflowStep])

  // Handle mapping decision
  const handleMappingDecision = useCallback((
    facetId: string,
    action: MappingAction,
    data?: Partial<FacetMappingDecision>
  ) => {
    addMappingDecision({
      facetId,
      action,
      decidedAt: new Date().toISOString(),
      ...data,
    } as FacetMappingDecision)
  }, [addMappingDecision])

  // Complete mapping - ensure all selected facets have decisions before applying
  const handleCompleteMapping = useCallback(async () => {
    debugLog('GenericInterviewer', 'handleCompleteMapping called')

    // Get latest state from store
    const store = useInterviewerStore.getState()
    const pendingDecisions = store.pendingMappingDecisions
    const source = store.currentSource

    if (!source) {
      debugLog('GenericInterviewer', 'No currentSource!')
      return
    }

    // Get selected facets: those in selectedFacetIds and not dismissed
    const activeFacets = source.facets?.filter(f => selectedFacetIds.has(f.id) && !f.dismissed) || []

    debugLog('GenericInterviewer', 'State check', {
      selectedFacets: activeFacets.length,
      pendingDecisions: pendingDecisions.length,
      selectedFacetIds: selectedFacetIds.size,
      totalFacets: source.facets?.length || 0,
    })

    // Safety net: ensure all selected facets have decisions
    const decisionFacetIds = new Set(pendingDecisions.map(d => d.facetId))
    const missingFacets = activeFacets.filter(f => !decisionFacetIds.has(f.id))

    if (missingFacets.length > 0) {
      debugLog('GenericInterviewer', `${missingFacets.length} facets missing decisions - creating defaults`)

      for (const facet of missingFacets) {
        const rawSuggestedType = facet.suggestedOntologyType || 'Problem'

        // FIX: Resolve the AI type to a valid entity type
        const resolved = resolveEntityType(rawSuggestedType)
        const entityType = resolved.entityType || 'Problem'  // Fallback if insight-only

        const label = facet.content.slice(0, 100)

        // Skip insight-only facets, create entities for others
        if (resolved.isInsightOnly) {
          addMappingDecision({
            facetId: facet.id,
            action: 'skip',
            decidedAt: new Date().toISOString(),
            aiConfidence: facet.confidence,
            aiSuggestedType: rawSuggestedType,
          })
        } else {
          addMappingDecision({
            facetId: facet.id,
            action: 'accept_suggestion',
            decidedAt: new Date().toISOString(),
            aiConfidence: facet.confidence,
            aiSuggestedType: rawSuggestedType,
            newEntityType: entityType,  // Use RESOLVED type
            newEntityLabel: label,
          })
        }
      }
    }

    debugLog('GenericInterviewer', `Calling applyMappingDecisions for source: ${source.id}`)
    await applyMappingDecisions(source.id)
    debugLog('GenericInterviewer', 'applyMappingDecisions completed')
  }, [selectedFacetIds, addMappingDecision, applyMappingDecisions])

  // Handle back/cancel
  const handleBack = useCallback(() => {
    if (workflowStep === 'detection') {
      setWorkflowStep('setup')
    } else if (workflowStep === 'mapping') {
      setWorkflowStep('detection')
    } else if (workflowStep === 'complete') {
      // Can't go back from complete
    } else {
      clearCurrentSource()
      resetWorkflow()
      onCancel()
    }
  }, [workflowStep, setWorkflowStep, clearCurrentSource, resetWorkflow, onCancel])

  // Handle done
  const handleDone = useCallback(() => {
    clearCurrentSource()
    resetWorkflow()
    onComplete()
  }, [clearCurrentSource, resetWorkflow, onComplete])

  // Get selected facets for mapping
  const selectedFacets = useMemo(() => {
    if (!currentSource?.facets) {
      console.log('[GenericInterviewer] selectedFacets: No facets in currentSource')
      return []
    }
    const filtered = currentSource.facets.filter(f => selectedFacetIds.has(f.id) && !f.dismissed)
    console.log(`[GenericInterviewer] selectedFacets: ${filtered.length} of ${currentSource.facets.length} facets (${selectedFacetIds.size} selected IDs, ${currentSource.facets.filter(f => f.dismissed).length} dismissed)`)
    return filtered
  }, [currentSource?.facets, selectedFacetIds])

  // Get current step index for progress
  const currentStepIndex = WORKFLOW_STEPS.findIndex(s => s.step === workflowStep)

  return (
    <div className="h-full flex flex-col">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {WORKFLOW_STEPS.map((s, index) => {
          const Icon = s.icon
          const isActive = s.step === workflowStep
          const isCompleted = index < currentStepIndex
          const isDisabled = index > currentStepIndex

          // Skip 'analyzing' in visual display (it's transient)
          if (s.step === 'analyzing' && workflowStep !== 'analyzing') return null

          return (
            <div key={s.step} className="flex items-center">
              {index > 0 && s.step !== 'analyzing' && WORKFLOW_STEPS[index - 1].step !== 'analyzing' && (
                <div className={cn(
                  'w-8 h-0.5 mx-1',
                  isCompleted ? 'bg-solution' : 'bg-warm-200'
                )} />
              )}
              <div className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                isActive && 'bg-solution/10 text-solution',
                isCompleted && 'bg-solution text-white',
                isDisabled && 'text-text-muted'
              )}>
                {isCompleted ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
                <span>{s.label}</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Main Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {/* Setup Step */}
        {workflowStep === 'setup' && (
          <InterviewSetup
            onSubmit={handleSetupSubmit}
            onCancel={onCancel}
          />
        )}

        {/* Analyzing Step */}
        {workflowStep === 'analyzing' && (
          <div className="h-full flex items-center justify-center p-8">
            <Card 
              className="p-8 text-center w-full max-w-md"
              style={{ animation: 'soft-pulse 3s ease-in-out infinite' }}
            >
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ animation: 'glow-pulse 2s ease-in-out infinite' }}
              >
                <Loader2 className="w-12 h-12 animate-spin text-solution" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Analyzing Interview</h3>
              <p className="text-sm text-text-muted">
                AI is extracting facets from your interview content. This may take a moment...
              </p>
            </Card>
          </div>
        )}

        {/* Detection Step */}
        {workflowStep === 'detection' && currentSource && (
          <div className="h-full flex gap-4">
            {void console.log(`[GenericInterviewer] DETECTION STEP - facets: ${currentSource.facets?.length}, selectedFacetIds: ${selectedFacetIds.size}`)}
            {/* Left: Transcript */}
            <div className="w-1/2 flex flex-col bg-warm-50 rounded-xl p-4 overflow-hidden">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Transcript
                </h3>
                {currentSource.linkedPersonName && (
                  <Badge variant="outline" className="text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    {currentSource.linkedPersonName}
                  </Badge>
                )}
              </div>
              <div className="flex-1 overflow-auto text-sm whitespace-pre-wrap text-text-secondary">
                {currentSource.rawContent}
              </div>
            </div>

            {/* Right: Facet Detection */}
            <div className="w-1/2 flex flex-col">
              <FacetDetectionPanel
                facets={currentSource.facets}
                selectedFacetIds={selectedFacetIds}
                onToggleFacet={handleToggleFacet}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
              />

              {/* Actions */}
              <div className="flex justify-between mt-4 pt-4 border-t border-warm-200">
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
                <Button
                  onClick={handleProceedToMapping}
                  disabled={selectedFacetIds.size === 0}
                  className="gap-2"
                >
                  Proceed to Mapping
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Mapping Step */}
        {workflowStep === 'mapping' && currentSource && (
          <div className="h-full flex flex-col">
            {void console.log(`[GenericInterviewer] MAPPING STEP - passing ${selectedFacets.length} facets to OntologyMappingPanel`)}
            {void console.log(`[GenericInterviewer] pendingMappingDecisions: ${pendingMappingDecisions.length}`)}
            <OntologyMappingPanel
              facets={selectedFacets}
              decisions={pendingMappingDecisions}
              onDecision={handleMappingDecision}
              onComplete={handleCompleteMapping}
            />
          </div>
        )}

        {/* Complete Step */}
        {workflowStep === 'complete' && currentSource && (
          <div className="h-full flex items-center justify-center p-8">
            <Card className="p-8 w-full max-w-2xl">
              <div className="flex items-start gap-6">
                {/* Icon */}
                <div className="w-16 h-16 rounded-full bg-solution/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-8 h-8 text-solution" />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">Interview Analysis Complete</h3>
                  <p className="text-sm text-text-muted mb-6">
                    Your interview has been analyzed and mapped to the ontology.
                  </p>

                  {/* Summary */}
                  <div className="bg-warm-50 rounded-lg p-4 mb-6">
                    <div className="text-sm font-medium mb-3">Summary</div>
                    <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
                      <div>
                        <span className="text-text-muted block">Interview Type</span>
                        <span className="font-medium">
                          {currentSource.interviewTypeId
                            ? getInterviewTypeConfig(currentSource.interviewTypeId).label
                            : 'Generic'}
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted block">Facets Analyzed</span>
                        <span className="font-medium">{selectedFacets.length}</span>
                      </div>
                      {currentSource.linkedPersonName && (
                        <div>
                          <span className="text-text-muted block">Linked Person</span>
                          <span className="font-medium">{currentSource.linkedPersonName}</span>
                        </div>
                      )}
                      {currentSource.mappingDecisions && (
                        <div>
                          <span className="text-text-muted block">Entities Created</span>
                          <span className="font-medium">
                            {getMappingSummary(currentSource.mappingDecisions).created +
                             getMappingSummary(currentSource.mappingDecisions).accepted}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => exportInterviewToPdf(currentSource as any)}
                      className="gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export PDF
                    </Button>
                    <Button onClick={handleDone} className="gap-2">
                      Done
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
